/**
 * InstagramContent zeigt eine Lister aller Posts für eine bestimmte PageId an
 * Inhalte werden direkt hier geladen
 * Token und Basepath wird dazu aus dem Storage geholt
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GeneralOptions, ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import Pagination from '@professocial/professocialFormComponents/FormComponents/Pagination'

import { Box, Grid, Skeleton, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { GetInstagramFeedResponse, InstagramPost } from '@3m5/sm_publish-frontend-api'
import { getInstagramFeeds } from '../../store/instagram/instagram.selector'
import { GetInstagramPageFeedRequest } from '@3m5/sm_publish-frontend-api'

import { getIgPageFeedInitiated } from '../../store/instagram/instagram.actions'
import { getGeneralOptions, getTheme, getUser } from '../../store/common/common.selectors'
import ImportProfessocialPost from './ImportProfessocialPost'
import useGalleryWidth from '../../util/useGalleryWidth'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import { useTranslation } from 'react-i18next'
import { getUserPages } from '@professocial/professocialCore/src/store/common/common.selectors'
import { NetworkType } from '@professocialApi/professocial-core-frontend-api'

export interface Props {
  networkPageId: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  uList: {
    display: 'flex',
    flexWrap: 'wrap',
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
}))

const InstagramContent: React.FC<Props> = (props) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()

  const theme = useSelector(getTheme)
  const user = useSelector(getUser)
  const userPages = useSelector(getUserPages)

  const [page, setPage] = useState(1)
  const [data, setData] = useState<InstagramPost[]>([])

  const storedInstagramData: GetInstagramFeedResponse | undefined = useSelector(getInstagramFeeds)
  const generalOptions: GeneralOptions | undefined = useSelector(getGeneralOptions)
  const dispatchGetInstagramFeeds = useCallback((params: GetInstagramPageFeedRequest) => dispatch(getIgPageFeedInitiated(params)), [dispatch])

  const mediaContainerRef = useRef<HTMLDivElement>(null)
  const storedInstagramPageId: string | null = window.localStorage.getItem('instagramPageNetworkId')

  const { itemsPerPage, elementSize } = useGalleryWidth(
    mediaContainerRef,
    data,
    193,
    generalOptions?.instagramNumberOfPosts || 24,
    generalOptions?.instagramNumberOfPosts === 9 ? 3 : undefined)

  const handlePageChange = (page = 1) => {
    if (storedInstagramData) {
      const startItem = (page - 1) * itemsPerPage
      const endItem = page * itemsPerPage
      storedInstagramData.posts && setData(storedInstagramData.posts.slice(startItem, endItem))
    }
  }

  useEffect(() => {
    handlePageChange()
  }, [storedInstagramData, itemsPerPage])

  useEffect(() => {
    handlePageChange(page)
  }, [page])

  useEffect(() => {
    const params: GetInstagramPageFeedRequest = {
      networkPageId: props.networkPageId,
    }
    dispatchGetInstagramFeeds(params)
  }, [props.networkPageId])

  const hasUserInstagramPage = useCallback(() => {
    return storedInstagramPageId !== null
  }, [userPages])


  return (
    <Grid container ref={mediaContainerRef}>
      {
        hasUserInstagramPage()
          ? (
            <>
              <Grid xs={12} item>
                <ul className={classes.uList}>
                  {storedInstagramData && data ? data.map((item: any, index: number) => {
                    return (
                      <li key={index}>
                        <ImportProfessocialPost
                          postConfiguration={{
                            theme: theme,
                            userRole: user?.userRole,
                            size: ScreenEnum.xl,
                            view: ViewEnum.overview,
                            networkPageId: props.networkPageId,
                            networkType: NetworkType.Instagram,
                            instagramPost: item,
                            elementSize: elementSize,
                          }}
                        />
                      </li>
                    )
                  }) : (
                    <>
                      {[...Array(itemsPerPage)].map((x, i) => (
                        <li key={i}><Skeleton style={{ margin: '2.5px' }} variant="rectangular" width={elementSize}
                          height={193}/></li>
                      ),
                      )}
                      <Box style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                        <Skeleton style={{ marginTop: '24px' }} variant="rectangular" width={305} height={25}/>
                      </Box>
                    </>
                  )}
                </ul>
                {storedInstagramData && storedInstagramData.posts.length === 0 &&
                  <NoDataComponent noDataText={t('networkContentMessages:noPostsYet')} alignment="center"
                    margin="16px 0"/>}
              </Grid>
              <Grid xs={12} item>
                <Box component="div" style={{ display: 'flex', justifyContent: 'center' }}>
                  {storedInstagramData && (
                    <Pagination
                      totalItems={storedInstagramData.posts.length}
                      itemsPerPage={itemsPerPage}
                      page={page}
                      setPage={setPage}
                    />
                  )}
                </Box>
              </Grid>
            </>
          )
          : null
      }

    </Grid>
  )
}

export default InstagramContent
